/**
 * Verkooporders uit Exact Online, klaargemaakt voor de pagina Bestellingen → Exact.
 *
 * Alles hier is puur: erin gaat de ruwe JSON van `/api/exact/orders`, eruit komt
 * wat de tabel en de detailpagina tonen. Dat is met opzet — de filter- en
 * backorderlogica is het enige stuk van deze pagina dat echt fout kan gaan, en
 * zo is het te toetsen zonder Exact of een ingelogd CMS (zie exactOrders.test.js).
 *
 * Twee dingen over de Exact-API die je moet weten als je hier iets aanpast:
 *
 * 1. **Datums komen binnen als `/Date(1699999999999)/`** — millisecondes sinds
 *    epoch in een string. Niet elke datum is gevuld (`DeliveryDate` is leeg tot
 *    er een leverdatum staat), dus parsen moet altijd `null` aankunnen. De oude
 *    pagina deed `data.match(/\(([^)]+)\)/)[1]` zonder controle en klapte eruit
 *    op een lege datum.
 * 2. **Orderregels komen als `{ results: [...] }`** bij een `$expand`, maar het
 *    losse order-endpoint kan ook een kale array teruggeven. `getOrderLines`
 *    vangt beide af.
 */

/** Exact-orderstatussen (SalesOrder.Status). Zelfde codes op order- en regelniveau. */
export const EXACT_ORDER_STATUS = {
  OPEN: 12,
  PARTIAL: 20,
  COMPLETE: 21,
  CANCELLED: 45,
};

/** Vertaalsleutel + tag-kleur per status. Labels staan in nl.json onder `exact.status`. */
export const EXACT_ORDER_STATUS_META = {
  [EXACT_ORDER_STATUS.OPEN]: { key: "open", color: "blue" },
  [EXACT_ORDER_STATUS.PARTIAL]: { key: "partial", color: "orange" },
  [EXACT_ORDER_STATUS.COMPLETE]: { key: "complete", color: "green" },
  [EXACT_ORDER_STATUS.CANCELLED]: { key: "cancelled", color: "default" },
};

/**
 * Levering en facturatie apart, met dezelfde codes als `Status`.
 *
 * Exact heeft drie statusvelden naast elkaar: `DeliveryStatus` (is het de deur
 * uit), `InvoiceStatus` (is het gefactureerd) en `Status` — dat laatste is de
 * **samenvatting** van de eerste twee. Een order die volledig geleverd is maar
 * nog niet gefactureerd, rolt daardoor op tot `Status = 20` (deels), terwijl er
 * qua levering niets meer openstaat.
 *
 * Dat leverde een scherm op waarin de statuskolom "Deels geleverd" zei en de
 * kolom Open op nul stond — allebei juist, maar ze gingen over iets anders.
 * Daarom tonen we de twee onderliggende velden en niet de samenvatting.
 */
export const EXACT_DELIVERY_STATUS_META = {
  [EXACT_ORDER_STATUS.OPEN]: { key: "not_delivered", color: "blue" },
  [EXACT_ORDER_STATUS.PARTIAL]: { key: "partial", color: "orange" },
  [EXACT_ORDER_STATUS.COMPLETE]: { key: "complete", color: "green" },
  [EXACT_ORDER_STATUS.CANCELLED]: { key: "cancelled", color: "default" },
};

export const EXACT_INVOICE_STATUS_META = {
  [EXACT_ORDER_STATUS.OPEN]: { key: "not_invoiced", color: "blue" },
  [EXACT_ORDER_STATUS.PARTIAL]: { key: "partial", color: "orange" },
  [EXACT_ORDER_STATUS.COMPLETE]: { key: "complete", color: "green" },
  [EXACT_ORDER_STATUS.CANCELLED]: { key: "cancelled", color: "default" },
};

/** Waarden van het leveringsfilter in de filterbalk. */
export const DELIVERY_FILTER = {
  ALL: "all",
  OUTSTANDING: "outstanding",
  BACKORDER: "backorder",
  DELIVERED: "delivered",
};

/**
 * Een Exact-datum naar millisecondes. Geeft `null` bij leeg of onleesbaar,
 * zodat de aanroeper zelf kan beslissen wat hij toont.
 */
export const parseExactDate = (value) => {
  if (!value) return null;
  if (typeof value === "number") return value;

  const dotNet = String(value).match(/\/Date\((-?\d+)/);
  if (dotNet) return Number(dotNet[1]);

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/** De orderregels als array, of een lege array. Vangt `{ results: [] }` én een kale array af. */
export const getOrderLines = (order) => {
  const lines = order?.SalesOrderLines;
  if (!lines) return [];
  if (Array.isArray(lines)) return lines;
  return Array.isArray(lines.results) ? lines.results : [];
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

/** Wat er van deze regel nog geleverd moet worden. Nooit negatief. */
export const getLineOpenQuantity = (line) =>
  Math.max(0, toNumber(line?.Quantity) - toNumber(line?.QuantityDelivered));

/**
 * Het ordertotaal. `AmountDC` van Exact is leidend — dat is het bedrag zoals het
 * in Exact staat, inclusief korting en vrachtregels. Alleen als dat veld
 * ontbreekt vallen we terug op de regels optellen, wat de oude pagina áltijd
 * deed en daardoor een ander bedrag toonde dan Exact zelf.
 */
export const getOrderAmount = (order) => {
  if (order?.AmountDC !== null && order?.AmountDC !== undefined && order?.AmountDC !== "") {
    return toNumber(order.AmountDC);
  }
  return getOrderLines(order).reduce((total, line) => total + toNumber(line.UnitPrice) * toNumber(line.Quantity), 0);
};

/**
 * Levering- of factuurstatus afgeleid uit de regels.
 *
 * Alleen als vangnet: `DeliveryStatus` / `InvoiceStatus` van Exact zijn leidend,
 * want die houden ook rekening met geannuleerde regels. Maar die velden kunnen
 * ontbreken — de API laat een veld weg zodra deze administratie het niet kent —
 * en dan is dit beter dan een leeg vakje.
 *
 * Geeft `null` als geen enkele regel het veld heeft: niets weten is iets anders
 * dan "nul geleverd", en dat verschil moet zichtbaar blijven.
 */
export const deriveLineStatus = (lines, field) => {
  const known = (lines || []).filter((line) => line?.[field] !== undefined && line?.[field] !== null);
  if (!known.length) return null;

  const ordered = lines.reduce((total, line) => total + toNumber(line.Quantity), 0);
  const done = known.reduce((total, line) => total + toNumber(line[field]), 0);

  if (done <= 0) return EXACT_ORDER_STATUS.OPEN;
  if (done >= ordered) return EXACT_ORDER_STATUS.COMPLETE;
  return EXACT_ORDER_STATUS.PARTIAL;
};

/**
 * Eén order teruggebracht tot wat de tabel en de filters nodig hebben.
 *
 * `customers` is de webshopklantenlijst; die levert de link naar de klant in het
 * CMS. De náám komt bij voorkeur uit Exact (`OrderedByName`), want een order die
 * in Exact zelf is ingetikt hoort niet bij een webshopaccount en toonde daardoor
 * eerder een streepje.
 */
export const summariseExactOrder = (order, customers = []) => {
  const lines = getOrderLines(order);
  const customer = customers?.find?.((c) => c.exactAccountGuid && c.exactAccountGuid === order.OrderedBy) || null;

  const orderedQuantity = lines.reduce((total, line) => total + toNumber(line.Quantity), 0);
  const deliveredQuantity = lines.reduce((total, line) => total + toNumber(line.QuantityDelivered), 0);
  const openQuantity = lines.reduce((total, line) => total + getLineOpenQuantity(line), 0);

  return {
    id: order.OrderID,
    orderNumber: order.OrderNumber ?? null,
    yourRef: order.YourRef || "",
    description: order.Description || "",
    status: order.Status ?? null,
    deliveryStatus: order.DeliveryStatus ?? deriveLineStatus(lines, "QuantityDelivered"),
    invoiceStatus: order.InvoiceStatus ?? deriveLineStatus(lines, "QuantityInvoiced"),
    orderDate: parseExactDate(order.OrderDate),
    deliveryDate: parseExactDate(order.DeliveryDate),
    customerGuid: order.OrderedBy || null,
    customerName: order.OrderedByName || customer?.company?.name || "",
    customerId: customer?.id || null,
    salesperson: order.SalespersonFullName || "",
    currency: order.Currency || "EUR",
    amount: getOrderAmount(order),
    lineCount: lines.length,
    orderedQuantity,
    deliveredQuantity,
    openQuantity,
    // Deels geleverd is de klassieke backorder: er is al iets de deur uit, de
    // rest staat nog open. Exact zet die order op status 20, maar we leiden het
    // ook uit de regels af zodat het klopt als de status nog niet is bijgewerkt.
    isBackorder: deliveredQuantity > 0 && openQuantity > 0,
    raw: order,
  };
};

/** Alle orders in één keer samenvatten. */
export const summariseExactOrders = (orders, customers = []) =>
  (Array.isArray(orders) ? orders : []).map((order) => summariseExactOrder(order, customers));

const matchesSearch = (summary, search) => {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;

  const haystack = [
    summary.orderNumber,
    summary.yourRef,
    summary.customerName,
    summary.description,
    summary.salesperson,
    ...getOrderLines(summary.raw).flatMap((line) => [line.ItemCode, line.ItemDescription, line.Description]),
  ];

  return haystack.some((value) => value && String(value).toLowerCase().includes(needle));
};

const matchesDelivery = (summary, delivery) => {
  switch (delivery) {
    case DELIVERY_FILTER.OUTSTANDING:
      return summary.openQuantity > 0;
    case DELIVERY_FILTER.BACKORDER:
      return summary.isBackorder;
    case DELIVERY_FILTER.DELIVERED:
      return summary.openQuantity === 0;
    default:
      return true;
  }
};

/**
 * De filterbalk toegepast op de samengevatte orders.
 *
 * `dateRange` is `[vanMs, totMs]`; beide grenzen tellen mee. Een lege of
 * ontbrekende waarde betekent "geen beperking", zodat de aanroeper niet overal
 * zelf hoeft te controleren of een filter aan staat.
 */
export const filterExactOrders = (summaries, filters = {}) => {
  const { search = "", customer = null, statuses = [], delivery = DELIVERY_FILTER.ALL, salesperson = null, dateRange = null } = filters;

  return (summaries || []).filter((summary) => {
    if (customer && summary.customerGuid !== customer) return false;
    if (statuses.length && !statuses.includes(summary.status)) return false;
    if (salesperson && summary.salesperson !== salesperson) return false;
    if (!matchesDelivery(summary, delivery)) return false;

    if (dateRange && (dateRange[0] || dateRange[1])) {
      if (summary.orderDate === null) return false;
      if (dateRange[0] && summary.orderDate < dateRange[0]) return false;
      if (dateRange[1] && summary.orderDate > dateRange[1]) return false;
    }

    return matchesSearch(summary, search);
  });
};

/** De klanten die in deze orders voorkomen, alfabetisch — voor het klantfilter. */
export const getCustomerOptions = (summaries) => {
  const byGuid = new Map();
  (summaries || []).forEach((summary) => {
    if (!summary.customerGuid || byGuid.has(summary.customerGuid)) return;
    byGuid.set(summary.customerGuid, { value: summary.customerGuid, label: summary.customerName || summary.customerGuid });
  });
  return [...byGuid.values()].sort((a, b) => a.label.localeCompare(b.label, "nl"));
};

/** De verkopers die in deze orders voorkomen, alfabetisch. */
export const getSalespersonOptions = (summaries) => {
  const names = new Set((summaries || []).map((summary) => summary.salesperson).filter(Boolean));
  return [...names].sort((a, b) => a.localeCompare(b, "nl")).map((name) => ({ value: name, label: name }));
};

/**
 * Per klant samengevat, voor het tabblad "Per klant".
 *
 * Dit is het antwoord op "staat er nog een backorder voor deze klant": één regel
 * per klant met wat er openstaat, hoe lang al, en hoeveel daarvan deels geleverd is.
 * Gesorteerd op openstaande stuks, want dat is waar iemand naar op zoek is.
 */
export const groupExactOrdersByCustomer = (summaries) => {
  const byGuid = new Map();

  (summaries || []).forEach((summary) => {
    const key = summary.customerGuid || summary.customerName || "onbekend";
    const group = byGuid.get(key) || {
      key,
      customerGuid: summary.customerGuid,
      customerName: summary.customerName || "",
      customerId: summary.customerId,
      orderCount: 0,
      backorderCount: 0,
      openQuantity: 0,
      amount: 0,
      oldestOrderDate: null,
    };

    group.orderCount += 1;
    if (summary.isBackorder) group.backorderCount += 1;
    group.openQuantity += summary.openQuantity;
    group.amount += summary.amount;
    if (summary.orderDate !== null && (group.oldestOrderDate === null || summary.orderDate < group.oldestOrderDate)) {
      group.oldestOrderDate = summary.orderDate;
    }

    byGuid.set(key, group);
  });

  return [...byGuid.values()].sort((a, b) => b.openQuantity - a.openQuantity || b.amount - a.amount);
};

/**
 * Waar een actie (printen, exporteren) op werkt: de aangevinkte rijen, of
 * anders alles wat er staat.
 *
 * Eén regel voor beide knoppen, want ze liepen uiteen: barcodes printen keek
 * naar de selectie en de Excel-export niet, die nam altijd de hele lijst. Dat
 * is precies het soort verschil dat je pas merkt als je het verkeerde bestand
 * al hebt rondgestuurd.
 *
 * De selectie wordt tegen de meegegeven rijen aangehouden — dat is de
 * gefilterde lijst — zodat een vinkje dat door een filterwijziging niet meer
 * zichtbaar is, ook niet stilletijk meegaat.
 */
export const getSelectedOrAll = (rows, selectedKeys) => {
  const list = rows || [];
  const keys = new Set(selectedKeys || []);
  if (!keys.size) return list;

  const selected = list.filter((row) => keys.has(row.id));
  return selected.length ? selected : list;
};

/** De kerncijfers boven het overzicht. */
export const getExactOrderTotals = (summaries) => {
  const list = summaries || [];
  return {
    orderCount: list.length,
    openCount: list.filter((summary) => summary.status === EXACT_ORDER_STATUS.OPEN).length,
    backorderCount: list.filter((summary) => summary.isBackorder).length,
    // Het aantal órders waar nog iets van moet, niet het aantal stuks: "er
    // staan nog 14 orders open" zegt meer over de werkvoorraad dan "er moeten
    // nog 1.240 stuks de deur uit". Zelfde maatstaf als het filter
    // DELIVERY_FILTER.OUTSTANDING, zodat de teller en dat filter altijd
    // hetzelfde aantal opleveren.
    outstandingOrderCount: list.filter((summary) => summary.openQuantity > 0).length,
    openQuantity: list.reduce((total, summary) => total + summary.openQuantity, 0),
    amount: list.reduce((total, summary) => total + summary.amount, 0),
    customerCount: new Set(list.map((summary) => summary.customerGuid).filter(Boolean)).size,
  };
};
